import * as ko from "knockout";
import {Cookie} from 'js-cookie';
import {Login} from "./Login";
import {SESSION_DURATION, SPINNER, TOKEN_KEY_SUPER} from "../constants";
import {IObjectData} from "../index";
import {global} from "../appContext";
import {createErrorMessage, getObjectDisplayName} from "../util/misc";
import PromiseRequest from "../req/PromiseRequest";
import {storeObservable} from "../util/sessionstore";
import {KOSubscriptionTracker} from "../util/KOSubscriptionTracker";
import {nsAmplify} from "../util/nsAmplify";

interface ISuperAdminData {
    displayName: string
    id: number
}

export class LoginManager {

    private readonly _superAdminData: KnockoutObservable<ISuperAdminData | null> = ko.observable(null);
    private req_emulatelogin: PromiseRequest = new PromiseRequest('emulatelogin');
    private productionWarning!: KnockoutObservable<boolean>;
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();

    constructor(private login: Login) {
    }

    /** Completes when automatic session restore (or anonymous startup) has settled. */
    init(): Promise<void> {
        this.productionWarning = storeObservable('production.warning');
        this.kosub.subscribe(this.sad, () => this.productionWarning(this.isUserDev()));
        this.kosub.subscribe(this.login.user, () => this.productionWarning(this.isUserDev()));

        const superToken = this.getSuperToken();
        const token = this.login.getToken();
        if (superToken) {
            if (!token) {
                this.login.setToken(superToken);
                this.removeSuperToken();
                return this.login.doLogin().catch(() => undefined);
            }
            this._superAdminData(this.getSuperAdminData());
        }
        return this.login.doStartup();
    }

    isUserSA() {
        return !!this.getSuperToken() || global.permissionValidator.superAdmin();
    }

    isUserDev(): boolean {
        const id = this.sad()?.id ?? this.login.user()?.id;
        if (id == null) {
            return false;
        }
        return [
            146698, 2, // manuel
            146699, 13, // flemming
            146703, 15, // sturle
            487671, 8, // sergon
        ].indexOf(id) != -1;
    }

    doEmulation(user: IObjectData) {
        // Hide app shell via flag (same as logout) — no blank `#/-` route.
        this.login.isLoggingOut(true);
        global.chromeReady(false);
        global.messageData({message: `((emulating login|td)) [${getObjectDisplayName(user)}] ${SPINNER}`});
        nsAmplify.store('lastHash', null)
        this.req_emulatelogin.abort();

        // store old values
        const old = {
            user: this.login.user(),
            organizations: this.login.organizations(),
            selectedOrganization: this.login.selectedOrganization(),
        }

        // flush current user login
        this.login.user(null)
        this.login.organizations([])
        this.login.selectedOrganization(null)

        this.req_emulatelogin
            .request({
                userid: user.id,
                orgnr: old.selectedOrganization?.organizationid,
            })
            .then((data: string[]) => {
                const token = data[0];
                if (!token) {
                    throw new Error('Missing emulate login token');
                }
                if (old.user) {
                    this.setSuperAdminToken(old.user, this.login.getToken());
                }
                this.login.setToken(token);
                void this.login.doLogin().finally(() => {
                    this.login.isLoggingOut(false);
                });
            })
            .catch((reason: string) => {
                // restore old values
                this.login.user(old.user)
                this.login.organizations(old.organizations)
                this.login.selectedOrganization(old.selectedOrganization)

                global.messageData(null);
                global.snackbarData(createErrorMessage(String(reason)));
                this.login.isLoggingOut(false);
            });
    }

    public sad = ko.pureComputed((): ISuperAdminData | null => {
        return this._superAdminData();
    });

    private getSuperToken() {
        return Cookie.get(TOKEN_KEY_SUPER);
    }

    public isEmulated(): boolean {
        return !!this.getSuperToken()
    }

    private getSuperAdminData(): ISuperAdminData {
        const displayName = Cookie.get('_sadn');
        const id = Cookie.get('_said');
        return {
            displayName: displayName ?? '',
            id: Number(id),
        };
    }

    private setSuperAdminToken(userObjectData: IObjectData, token: string) {
        const expires = {expires: new Date(new Date().getTime() + SESSION_DURATION)};
        const displayName = getObjectDisplayName(userObjectData);
        const id = String(userObjectData.id);
        Cookie.set(TOKEN_KEY_SUPER, token, expires);
        Cookie.set('_sadn', displayName ?? '', expires);
        Cookie.set('_said', id, expires);
        this._superAdminData(this.getSuperAdminData());
    }

    private removeSuperToken() {
        Cookie.remove(TOKEN_KEY_SUPER);
        Cookie.remove('_sadn');
        Cookie.remove('_said');
        this._superAdminData(null);
    }

}
